/**
 * PE Diligence TUI Web - Type Definitions
 */

// Private company metadata (optional details to improve research)
export interface PrivateCompanyMetadata {
  hqLocation?: string
  estimatedSize?: string
  estimatedRevenue?: string
  sector?: string
  linkedinUrl?: string
  website?: string
}

// Company data types
export interface CompanyData {
  ticker: string
  name: string
  sector: string
  industry: string
  marketCap: number
  description?: string
  quoteType?: 'EQUITY' | 'ETF' | 'MUTUALFUND' | 'PRIVATE'
  isPrivate?: boolean
  privateMetadata?: PrivateCompanyMetadata
}

// Tab configuration
export interface Tab {
  id: string
  label: string
  shortcut: string
}

// Financial data
export interface FinancialData {
  year: number
  revenue: number
  netIncome: number
  fcf: number
  assets: number
  liabilities: number
  equity: number
}

// FCF prediction
export interface FCFPrediction {
  year: number
  predicted: number
  lower: number
  upper: number
  confidence: number
}

// Regime classification
export interface RegimeClassification {
  regime: 'growth' | 'stable' | 'transition' | 'distressed'
  confidence: number
  stability: number
  trend: 'improving' | 'stable' | 'declining'
}

// Chat message
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: Record<string, unknown>
}

// M&A thesis
export interface MAThesis {
  id: string
  name: string
  criteria: {
    minRevenue: number
    maxRevenue: number
    minFcfMargin: number
    sectors: string[]
    regimes: string[]
  }
}

// Report configuration
export interface ReportConfig {
  type: 'quick' | 'full' | 'custom'
  sections: {
    executiveSummary: boolean
    financialAnalysis: boolean
    fcfProjections: boolean
    regimeAnalysis: boolean
    riskAssessment: boolean
    competitorAnalysis: boolean
    managementAssessment: boolean
    valuationAnalysis: boolean
  }
}

// Report section status
export interface ReportSection {
  id: string
  name: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: number
}

// Report
export interface Report {
  id: string
  ticker: string
  companyName: string
  reportType: 'quick' | 'full' | 'custom'
  createdAt: string
  status: 'generating' | 'completed' | 'failed'
  filePath?: string
}

// Status bar state
export interface StatusState {
  message: string
  type: 'info' | 'success' | 'warning' | 'error' | 'loading'
}

// Analysis history entry
export interface AnalysisEntry {
  id: string
  ticker: string
  companyName: string
  analyzedAt: string
  status: 'completed' | 'failed'
}

// ============================================================================
// Report Quality Evaluation Types
// ============================================================================

// Individual quality suggestion for report improvement
export interface QualitySuggestion {
  section: string                // Which section needs work
  issue: 'low_accuracy' | 'low_relevance' | 'stale_data' | 'weak_sources' | 'missing_data'
  severity: 'critical' | 'high' | 'medium' | 'low'
  suggestion: string             // Human-readable recommendation
  firecrawlQuery?: string        // Optional query for gap-filling
}

// Overall report quality assessment
export interface ReportQuality {
  // Overall metrics
  overallScore: number           // 0-100 weighted average
  grade: 'A' | 'B' | 'C' | 'D' | 'F'

  // Breakdown
  completeness: number           // % of sections with confidence > 70%
  averageAccuracy: number        // Mean accuracy across sections
  averageRelevance: number       // Mean relevance across sections
  averageRecency: number         // Mean recency across sections
  averageSourceTrust: number     // Mean source trust across sections

  // Issues
  totalConflicts: number         // Sum of conflicts across sections
  totalGaps: number              // Sum of gaps across sections
  weakSections: string[]         // Sections with confidence < 60%

  // Recommendations
  suggestions: QualitySuggestion[]
  canImprove: boolean            // True if Firecrawl could help
}

// LLM source evaluation (from Azure backend)
export interface SourceEvaluation {
  accuracy: number
  relevance: number
  recency: number
  sourceTrust: number
  overall: number
  reasoning: string
  gaps: string[]
}

// Dual-model consensus result (from Azure /api/evaluate-sources)
export interface ConsensusResult {
  primary_eval: SourceEvaluation
  secondary_eval: SourceEvaluation
  model_agreement: number        // 0-100: how similar the models' scores are
  consensus_score: number        // Average of overall scores
  conflicts: string[]            // Metrics where models disagree by >20 points
  recommendation: 'high' | 'medium' | 'low' | 'review'
  gaps: string[]                 // Merged gaps from both models
  // Legacy fields for backward compatibility
  kimi_eval?: SourceEvaluation
  grok_eval?: SourceEvaluation
  models_used?: string[]
}

// Deal pipeline entry
export interface Deal {
  id: string
  ticker: string
  companyName: string
  stage: 'screening' | 'initial_dd' | 'deep_dive' | 'negotiation' | 'closed'
  lastActivity: string
  score?: number
}

// Default tabs configuration
export const DEFAULT_TABS: Tab[] = [
  { id: 'financials', label: 'Financials', shortcut: '1' },
  { id: 'fcf', label: 'FCF', shortcut: '2' },
  { id: 'regime', label: 'Regime', shortcut: '3' },
  { id: 'ma', label: 'M&A', shortcut: '4' },
  { id: 'report', label: 'Report', shortcut: '5' },
  { id: 'dataroom', label: 'Data Room', shortcut: '6' },
  { id: 'history', label: 'History', shortcut: '7' },
  // Chat hidden temporarily - will be renumbered to 8 when re-enabled
  // { id: 'chat', label: 'Chat', shortcut: '8' },
]
